import {concat, emptyArray} from '@packages/utilities/fp/utils';
import {
  type ExpressionPath,
  type ExpressionPathPart,
  type Type,
  type DataTypeSystem,
  matchExpression,
  VariableMap,
  Expressions,
  EnvTypes,
  Variable,
  Types,
  Plugins,
  getSystem,
  withSystem,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
  getAnn,
  updateAnn,
} from '@packages/utilities/contextual-expression';

import {append, prepend} from '@packages/utilities/array';
import {type Expression, TRecord} from '@packages/systems/wfdl-ast';
import {
  type Binding,
  type AvailableBindings,
} from '@packages/systems/wfdl-findBindings';
import {type Lazy, force} from '@packages/utilities/lazy';
import {
  type Identifier,
  identifierToString,
} from '@packages/systems/wfdl-Qualified';
import {FolderDefaultIcon} from '@packages/systems/spring';
import {
  reduce as reduceLazyList,
  fromArray,
} from '@packages/utilities/lazy-list';
import {reportBugOnce} from '@packages/utilities/reporter';
import type {
  ExpressionEditorActionPayload,
  ExpressionEditorDispatch,
} from './types';
import type {BindingPathMapPojo} from '@packages/systems/dynamo/binding-types';

export type {
  ExpressionEditorActionPayload,
  ExpressionBindMetaPayload,
  ExpressionEditorDispatch,
} from './types';
export {
  ExpressionEditor,
  ExpressionBinding,
  ExpressionEditorContext,
  TypeEditor,
  ExpressionEditorImplementationContext,
  useExpressionEditorContext,
  useExpressionEditorImplementationContext,
} from './ExpressionEditorContext';
export type {
  ExpressionEditorImplementationProps,
  ExpressionBindingImplementationProps,
} from './ExpressionEditorContext';
export {getListOfNodesFromString} from './childrenPrimitiveTypeUtils';

/**
 * These utilities are used as helper functions for calling dispatch functions
 * from Type Alias render without forcing the author to write out the full
 * actions.
 */

/**
 * Creates an action for updating an expression. Use this function for custom UI
 * onChange handling, ie non-ExpressionEditor.
 */
export const updateExpressionAction = (
  expr: Expression,
  meta?: {
    analytics?: {
      type: 'element-settings-updated' | 'visibility-changed';
      value: string;
    };
  }
): ExpressionEditorActionPayload => ({
  type: 'EXPRESSION_ACTION',
  key: [],
  payload: {
    newExpression: expr,
    meta,
  },
});

/**
 * Creates an action for updating an expression at an Expression Path. Use this
 * function for custom UI onChange handling, ie non-ExpressionEditor.
 */
export const updateExpressionAtAction = (
  expr: Expression,
  path: ExpressionPath
): ExpressionEditorActionPayload => ({
  key: path,
  type: 'EXPRESSION_ACTION',
  payload: {
    newExpression: expr,
  },
});

/**
 * Creates an action for updating multiple expressions at once. Each change.key
 * is relative to the top-level key prefix (empty by default). Use this when
 * several fields must stay in sync without rewriting the entire element data.
 */
export const updateExpressionsAction = (
  changes: Array<{
    key: ExpressionPath;
    newExpression: Expression;
  }>
): ExpressionEditorActionPayload => ({
  type: 'EXPRESSIONS_CHANGED',
  key: [],
  payload: {
    changes,
  },
});

/**
 * Extends the path of the action that comes from ExpressionEditor with the
 * provided ExpressionPathPart.
 */
export const forwardDispatch =
  (dispatch: ExpressionEditorDispatch) =>
  (part: ExpressionPathPart) =>
  (action: ExpressionEditorActionPayload) => {
    dispatch({
      ...action,
      key: prepend(part, action.key || emptyArray),
    });
  };

/**
 * Extends the action that comes from ExpressionEditor to target a specific
 * path. Use this function for all inner ExpressionEditor dispatch calls from
 * Type Aliases.
 */
export const extendExpressionEditorActionAtPath =
  (dispatch: ExpressionEditorDispatch) =>
  (path: ExpressionPath) =>
  (action: ExpressionEditorActionPayload) => {
    dispatch({
      ...action,
      key: concat(action.key)(path),
    });
  };

/**
 * Change the Expression that the action payload is sending. For example when
 * rendering a Number input but you want to store a Text Expression you could
 * use this utility. Use this function for inner ExpressionEditor dispatch calls
 * from Type Aliases.
 */
export const updateExpressionEditorAction =
  (dispatch: ExpressionEditorDispatch) =>
  (expr: Expression) =>
  (action: ExpressionEditorActionPayload) => {
    if (action.type === 'EXPRESSION_ACTION') {
      dispatch({
        ...action,
        payload: {
          ...action.payload,
          newExpression: expr,
        },
      });
    } else {
      dispatch(action);
    }
  };

/**
 * True when an env-value or env-type marks a function as taking an implicit
 * final argument — either prefilled args bound to a value, or a hole awaiting
 * binding. Both shapes hide the last argument in the editor.
 */
export const isImplicitFunction = (
  envValue: {implicit?: string | null} | null | undefined
): boolean =>
  envValue?.implicit === 'prefilledArgs' || envValue?.implicit === 'holeArg';

const getBindingExpression = (
  envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>,
  expression: Expression,
  // If we have an implicit function we need to store the first argument
  // in here so that we can return that
  implicitArg?: Expression
): Expression => {
  // if we have an implicit function selected we want to return the stored
  // implicitArg as envType
  const val = Expressions.getVariable(expression);
  const envType = val ? VariableMap.get(envTypes, val) : null;
  if (envType && isImplicitFunction(envType) && implicitArg) {
    return getBindingExpression(envTypes, implicitArg);
  }

  const call = Expressions.getCall(expression);
  if (call) {
    return getBindingExpression(
      envTypes,
      call.fun,
      // If we have nested function calls we still want the first
      // implicit argument to be persisted because in wfdl the
      // last function calls argument comes first
      implicitArg ? implicitArg : call.arg
    );
  }

  return expression;
};

export type BindingIdAndPath = {
  id: Variable.VariableType;
  path: string[];
};

export function getBindingIdAndPath(
  envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>,
  expression: Expression
): BindingIdAndPath | null {
  const expr = getBindingExpression(envTypes, expression);
  const {from, path} = toSelectPath(expr);
  const id = Expressions.getVariable(from);
  if (!id) return null;
  return {id, path};
}

// Given an expression, assumed to be a value from env
// return its type
export const getBindingType = (
  // Current env value types in scope
  envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>,
  // Expression for which we are looking for a type
  expression: Expression
): Type | null => {
  const idAndPath = getBindingIdAndPath(envTypes, expression);

  if (!idAndPath) return null;

  const {id, path} = idAndPath;

  let currentDataType = EnvTypes.getType(envTypes, id);

  for (const pathSegment of path) {
    if (!currentDataType) break;
    const fields = Types.getRecordFields(currentDataType);
    if (!fields) {
      // If we encounter a non-record in our dataType path then we just ignore
      // this path segment and report an error:
      // Please see https://github.com/webflow/webflow/pull/39900
      // eslint-disable-next-line webflow/better-bug-reporting
      reportBugOnce(
        'ExpressionBinding:getBindingIcon:UnexpectedDataType',
        `Expected "Record" but got "${
          currentDataType ? Types.print(currentDataType) : 'no type!'
        }"`,
        {
          envTypes,
          expression,
        }
      );
      return null;
    }
    // @ts-expect-error - TS2322 - Type 'Type | undefined' is not assignable to type 'Type | null'.
    currentDataType = fields[pathSegment];
  }

  if (!currentDataType) return null;
  return currentDataType;
};

export const getBindingIcon = (
  envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>,
  expression: Expression,
  plugins: Plugins.PluginsType
): Plugins.QualifiedPluginIcon | null => {
  const type = getBindingType(envTypes, expression);
  if (!type) {
    return null;
  }
  return Types.getIcon(type, plugins);
};

// Given an Expression, assumed to be a value from env,
// and determine a label string for it.
export const getBindingLabel = (
  // Current env value types in scope
  envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>,
  // Expression for which we are looking for a label
  expression: Expression
): string => {
  const idAndPath = getBindingIdAndPath(envTypes, expression);

  if (!idAndPath) return '';

  const {id, path} = idAndPath;

  let currentDataType = EnvTypes.getType(envTypes, id);

  if (!path.length) {
    const label = currentDataType ? Types.getLabel(currentDataType) : '';
    return label || Variable.getName(id);
  }

  const labelSegments: string[] = [];
  for (const pathSegment of path) {
    if (!currentDataType) {
      labelSegments.push(pathSegment);
      continue;
    }

    const fields = Types.getRecordFields(currentDataType);
    if (!fields) {
      // If we encounter a non-record in our dataType path then we just ignore
      // this path segment and report an error:
      // Please see https://github.com/webflow/webflow/pull/39900
      // eslint-disable-next-line webflow/better-bug-reporting
      reportBugOnce(
        'ExpressionBinding:bindingLabel:UnexpectedDataType',
        `Expected "Record" but got "${
          currentDataType ? Types.print(currentDataType) : 'no type!'
        }"`,
        {
          envTypes,
          expression,
        }
      );
      currentDataType = null;
      continue;
    }
    currentDataType = fields[pathSegment] || null;
    const label =
      (currentDataType && Types.getLabel(currentDataType)) || pathSegment;
    labelSegments.push(label);
  }
  return labelSegments.join('.');
};

/**
 * Get a `system.label` to use in the binding menu for the given expression.
 *
 * Differences between this and `getBindingLabel`:
 * * `getBindingLabel` would return `user.address.city` when
 * `getBindingMenuLabel` would only return `city`
 * * `getBindingMenuLabel` returns empty string in the absence of `system.label`
 */
export const getBindingMenuLabel = (
  // Current env value types in scope
  envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>,
  // Expression for which we are looking for a label
  expression: Expression
): string => {
  const type = getBindingType(envTypes, expression);
  if (!type) return '';
  return Types.getLabel(type) || '';
};

type SelectPath = {from: Expression; path: string[]};

export const toSelectPath = (expression: Expression): SelectPath =>
  matchExpression<SelectPath>({
    Select: ({from, prop}) => {
      const inner = toSelectPath(from);
      return {from: inner.from, path: append(inner.path, prop)};
    },
    _: (expr) => ({from: expr, path: emptyArray}),
  })(expression);

/**
 * Given an expression, return the `Select` expression corresponding to a
 * component-local field (i.e. a `Select` with a `Variable` bound to `"data"`)
 */
export const getFieldId = (expression: Expression): string | null => {
  if (!expression) {
    return null;
  }
  const {from, path} = toSelectPath(expression);

  if (Expressions.isDataVariable(from) && path.length === 1) {
    return path[0] ?? null;
  }

  // If the expression is a call, we want to check whether the argument has a field id
  const callExpression = Expressions.getCall(expression);
  if (callExpression) {
    return getFieldId(callExpression.arg);
  }

  return null;
};

export const fieldWrapperLabel = (label: string | null) =>
  `ExpressionEditor-fieldWrapper-${label ?? ''}`;

export function updateTypeSystem(
  expr: ContextualExpression,
  systemUpdater: (system: DataTypeSystem<unknown>) => DataTypeSystem<unknown>
): ContextualExpression {
  return updateAnn((ann) => {
    const type = ann.type;
    if (!type) {
      // Please see https://github.com/webflow/webflow/pull/39900
      // eslint-disable-next-line webflow/better-bug-reporting
      reportBugOnce('updateTypeSystem: TypeNotFound', 'Type not found on ann', {
        ann,
      });
      return ann;
    }

    const system = getSystem(type);

    return {
      type: withSystem(system ? systemUpdater(system) : {}, type),
      env: ann.env,
      errors: ann.errors,
    };
  }, expr);
}

/**
 * Removes the `private` system property
 */
export const removePrivateSystemProperty = (
  expr: ContextualExpression
): ContextualExpression => {
  return updateTypeSystem(expr, (system) => ({...system, private: false}));
};

export const isDisabledByDynamoBinding = (
  dynBoundFieldPaths: BindingPathMapPojo,
  exprPath: ExpressionPath,
  value: any
): boolean => {
  const dynBoundFieldPathKeys =
    dynBoundFieldPaths && Object.keys(dynBoundFieldPaths);

  if (!dynBoundFieldPathKeys) {
    return false;
  }
  if (exprPath.length === 0) {
    return false;
  }

  const atPath: Array<any | string> = [];

  for (const pathPart of exprPath) {
    if (pathPart.in !== 'Record') {
      return false;
    }
    atPath.push(pathPart.at);
  }

  const at = atPath.join('.');

  // Children
  if (at === 'children') {
    return dynBoundFieldPathKeys.some((fieldPath) => fieldPath === 'innerHTML');
  }

  // Image
  if (at === 'img') {
    return dynBoundFieldPathKeys.some((fieldPath) => fieldPath === 'src');
  }

  if (at === 'link') {
    return (
      // Forces the binding UI to be disabled even if there's technically no
      // binding yet, but the `dynpage` option is selected:
      (value && value.mode === 'dynpage') ||
      dynBoundFieldPathKeys.some(
        (fieldPath) => fieldPath === 'href' || fieldPath === 'dataWHref'
      )
    );
  }

  if (at === 'video') {
    return dynBoundFieldPathKeys.some((fieldPath) => fieldPath === 'innerHTML');
  }

  if (at === 'json.items') {
    return dynBoundFieldPathKeys.some((fieldPath) => fieldPath === 'media');
  }

  return false;
};

export const getFunctionCallArgumentsForRendering = (
  expr: ContextualExpression
): Array<ContextualExpression> => {
  const call = ContextualExpressions.deconstructCall(expr);
  const fnVariable = ContextualExpressions.getVariable(stripAnn(call.fun));
  const fnValue = fnVariable
    ? VariableMap.get(getAnn(call.fun).env.context.values, fnVariable)
    : null;

  // If we are rendering the arguments of an implicit function we don't
  // want to render the last argument, which is the implicitly bound value.
  const args =
    fnValue &&
    (fnValue.implicit === 'prefilledArgs' || fnValue.implicit === 'holeArg')
      ? call.args.slice(0, -1)
      : call.args;
  return args;
};

/**
 * Restructures a binding's children so that entries with grouped labels
 * (e.g. "Foo/Bar") become nested under a group binding keyed by the group name.
 * Ungrouped entries are kept at the top level.
 *
 * Also strips group prefixes from field labels in envTypes so that grouped
 * items display just the property name (e.g. "Bar") instead of "Foo/Bar".
 */
export const groupBindingChildren = (
  binding: Binding,
  envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>,
  variable: Variable.VariableType,
  componentName: string | null
): {
  binding: Binding;
  envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>;
} => {
  const envEntry = VariableMap.get(envTypes, variable);
  const varType = envEntry?.dataType ?? null;
  const recordFields = varType ? Types.getRecordFields(varType) : null;

  const groupedChildren = new Map<string, Binding>();
  let updatedFields: {[key: string]: Type} | null = null;

  for (const [key, childBinding] of binding.children.entries()) {
    const fieldType = recordFields?.[key];
    const label = fieldType ? Types.getLabel(fieldType) : key;
    const fullLabel = label || key;
    const delimiterIndex = fullLabel.lastIndexOf('/');
    const groupName =
      delimiterIndex !== -1
        ? fullLabel.substring(0, delimiterIndex).trim() || null
        : null;
    const propertyName = fullLabel.substring(delimiterIndex + 1).trim();

    if (groupName) {
      const existing = groupedChildren.get(groupName);
      const existingChildren = existing?.children ?? new Map<string, Binding>();
      const updatedChildren = new Map(existingChildren);
      updatedChildren.set(key, childBinding);

      groupedChildren.set(groupName, {
        expr: null,
        implicits: emptyArray,
        children: updatedChildren,
        icon: FolderDefaultIcon,
        expandOnInitialOpen: true,
      });

      // Strip the group prefix from the field label in the type
      if (fieldType) {
        if (!updatedFields) updatedFields = {...recordFields};
        updatedFields[key] = withSystem(
          {...getSystem(fieldType), label: propertyName},
          fieldType
        );
      }
    } else {
      groupedChildren.set(key, childBinding);
    }
  }

  let updatedEnvTypes = envTypes;
  if (envEntry && varType) {
    const fields = updatedFields ?? recordFields;
    // Rebuild the data type with stripped field labels and the component name
    // so the binding menu shows the component name as the top-level heading.
    let updatedType = fields ? TRecord(fields) : varType;
    if (componentName) {
      updatedType = withSystem(
        {...getSystem(varType), label: componentName},
        updatedType
      );
    }
    updatedEnvTypes = VariableMap.insert(envTypes, variable, {
      ...envEntry,
      dataType: updatedType,
    });
  }

  return {
    binding: {
      ...binding,
      children: groupedChildren,
    },
    envTypes: updatedEnvTypes,
  };
};

/**
 * Resolves available bindings and prepares them for the binding menu:
 * - Groups "data" properties with "/" labels into nested collapsible nodes
 * - Strips group prefixes from field labels in envTypes
 * - Sets the component name on the data variable type
 * - Ensures the "data" entry appears first among local bindings
 */
export const resolveBindingOptions = (
  availableBindings: Lazy<AvailableBindings>,
  envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>,
  componentName: string | null
): {
  bindings: AvailableBindings;
  envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>;
} => {
  const bindings = force(availableBindings);

  let menuEnvTypes = envTypes;
  const entries = reduceLazyList(
    bindings.local,
    (acc: [Identifier, Binding][], [name, binding]: [Identifier, Binding]) => {
      if (identifierToString(name) === 'data') {
        const result = groupBindingChildren(
          binding,
          menuEnvTypes,
          Variable.unqualified(name),
          componentName
        );
        menuEnvTypes = result.envTypes;
        acc.unshift([name, result.binding]);
      } else {
        acc.push([name, binding]);
      }
      return acc;
    },
    []
  );

  const updatedBindings: AvailableBindings = {
    plugins: bindings.plugins,
    local: fromArray(entries),
  };

  return {bindings: updatedBindings, envTypes: menuEnvTypes};
};
